import type * as highcharts from "highcharts";
import React from "react";
import { useQuery } from "react-query";
import { LoadingDots } from "web-ui";

import { getReleaseHistory } from "api/dashboardBackend";
import type {
  ReleaseHistoryData,
  ReleaseHistoryEntry,
} from "api/dashboardBackend";
import DuoErrorScreen, { DuoErrorType } from "components/DuoErrorScreen";
import SortableTable, { type Column } from "components/SortableTable";
import styles from "components/history/ReleaseHistoryContent.module.scss";
import Chart, { ChartType } from "components/release/metric-chart/Chart";
import { generateMappingFromAppBuild } from "components/release/useful-links/helpers";
import useErrorScreen from "components/useErrorScreen";
import { namedColor } from "util/color";
import { CHINA_STORES, PlatformId } from "util/platform";
import { compareSemVers, semVerString } from "util/version";

const NOT_AVAILABLE = "N/A";

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "numeric",
  weekday: "short",
  year: "2-digit",
};

const TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: "numeric",
  hour12: false,
  minute: "numeric",
};
const formatDateTime = (timestamp: number | undefined): string => {
  if (!timestamp) {
    return NOT_AVAILABLE;
  }
  const date = new Date(timestamp * 1000);
  return `${date.toLocaleString("en-US", DATE_FORMAT_OPTIONS)} ${date.toLocaleString(
    "en-US",
    TIME_FORMAT_OPTIONS,
  )}`;
};

const versionColumn: Column<ReleaseHistoryEntry> = {
  compare: (a, b) => compareSemVers(a.version, b.version),
  getCell: d => semVerString(d.version),
  getValue: d => (d.version ? semVerString(d.version) : NOT_AVAILABLE),
  header: "Version",
};
const timestampKickoffColumn: Column<ReleaseHistoryEntry> = {
  getCell: d => formatDateTime(d.timestampKickoff),
  getValue: d =>
    d.timestampKickoff ? new Date(d.timestampKickoff).valueOf() : 0,
  header: "Kickoff Time (local)",
};

const timestampCompleteColumn: Column<ReleaseHistoryEntry> = {
  getCell: d => formatDateTime(d.timestampComplete),
  getValue: d =>
    d.timestampComplete ? new Date(d.timestampComplete).valueOf() : 0,
  header: "Complete (local)",
};

const timestamp1PercentColumn = (
  platformId: PlatformId,
): Column<ReleaseHistoryEntry> => ({
  getCell: d => formatDateTime(d.timestamp1Percent),
  getValue: d =>
    d.timestamp1Percent ? new Date(d.timestamp1Percent).valueOf() : 0,
  header: "1% Rollout (local)",
  isHidden: platformId !== PlatformId.IOS,
});

const timestamp2PercentColumn = (
  platformId: PlatformId,
): Column<ReleaseHistoryEntry> => ({
  getCell: d => formatDateTime(d.timestamp2Percent),
  getValue: d =>
    d.timestamp2Percent ? new Date(d.timestamp2Percent).valueOf() : 0,
  header: "2% Rollout (local)",
  isHidden: platformId !== PlatformId.IOS,
});

const timestamp10PercentColumn = (
  platformId: PlatformId,
): Column<ReleaseHistoryEntry> => ({
  getCell: d => formatDateTime(d.timestamp10Percent),
  getValue: d =>
    d.timestamp10Percent ? new Date(d.timestamp10Percent).valueOf() : 0,
  header: "10% Rollout (local)",
  isHidden: platformId !== PlatformId.Android,
});

const timestamp99PercentColumn = (
  platformId: PlatformId,
): Column<ReleaseHistoryEntry> => ({
  getCell: d => formatDateTime(d.timestamp99Percent),
  getValue: d =>
    d.timestamp99Percent ? new Date(d.timestamp99Percent).valueOf() : 0,
  header: "99% Rollout (local)",
  isHidden: platformId !== PlatformId.Android,
});

const statusColumn: Column<ReleaseHistoryEntry> = {
  getCell: d => d.status,
  getValue: d => d.status,
  header: "Status",
};

const releaseManagerColumn: Column<ReleaseHistoryEntry> = {
  getCell: d => d.releaseManagerName ?? NOT_AVAILABLE,
  getValue: d => d.releaseManagerName ?? NOT_AVAILABLE,
  header: "Release Manager",
};

const qaManagerColumn: Column<ReleaseHistoryEntry> = {
  getCell: d => d.qaManagerName ?? NOT_AVAILABLE,
  getValue: d => d.qaManagerName ?? NOT_AVAILABLE,
  header: "QA Manager",
};

const downloadLinkColumn = (
  platformId: PlatformId,
): Column<ReleaseHistoryEntry> => ({
  compare: (a, b) => compareSemVers(a.version, b.version),
  getCell: d => {
    if (!d.appBuildUrl) {
      return NOT_AVAILABLE;
    }

    // Show five links separated by | for Chinese Android build.
    if (platformId === PlatformId.China) {
      const baseUrl = d.appBuildUrl.replace(".apk", "");
      return (
        <>
          {CHINA_STORES.map((vendor, index) => (
            <React.Fragment key={vendor}>
              {index > 0 && " | "}
              <a
                href={`${baseUrl}_${vendor}.apk`}
                rel="noreferrer"
                target="_blank"
              >
                {vendor}
              </a>
            </React.Fragment>
          ))}
        </>
      );
    }

    // Default case for non-Chinese builds
    return (
      <a href={d.appBuildUrl} rel="noreferrer" target="_blank">
        Link
      </a>
    );
  },
  getValue: d => (d.version ? semVerString(d.version) : NOT_AVAILABLE),
  header: "Build",
  isHidden: platformId === PlatformId.Web,
});

const downloadMappingColumn = (
  platformId: PlatformId,
): Column<ReleaseHistoryEntry> => ({
  compare: (a, b) => compareSemVers(a.version, b.version),
  getCell: d =>
    d.appBuildUrl ? (
      <a
        href={generateMappingFromAppBuild(d.appBuildUrl)}
        rel="noreferrer"
        target="_blank"
      >
        Link
      </a>
    ) : (
      NOT_AVAILABLE
    ),
  getValue: d => (d.version ? semVerString(d.version) : NOT_AVAILABLE),
  header: "Mapping",
  isHidden: platformId !== PlatformId.Android,
});

const patchCountColumn = (
  platformId: PlatformId,
): Column<ReleaseHistoryEntry> => ({
  getCell: d => d.version.patch?.toString() ?? NOT_AVAILABLE,
  getValue: d => d.version.patch ?? -1,
  header: platformId === PlatformId.IOS ? "Hotfix" : "Patch",
});

const buildNumberColumn = (
  platformId: PlatformId,
): Column<ReleaseHistoryEntry> => ({
  getCell: d => d.version.build?.toString() ?? NOT_AVAILABLE,
  getValue: d => d.version.build ?? -1,
  header: "Patch",
  isHidden: platformId !== PlatformId.IOS,
});

const daysToCompleteColumn = (
  platformId: PlatformId,
): Column<ReleaseHistoryEntry> => ({
  getCell: d => {
    const endTime =
      platformId === PlatformId.Android
        ? d.timestamp99Percent
        : d.timestampComplete;
    const diffDays = calculateDaysElapsed(endTime, d.timestampKickoff);
    return diffDays >= 0 ? `${diffDays.toFixed(1)} days` : NOT_AVAILABLE;
  },
  getValue: d => {
    const endTime =
      platformId === PlatformId.Android
        ? d.timestamp99Percent
        : d.timestampComplete;
    return calculateDaysElapsed(endTime, d.timestampKickoff);
  },
  header: "Rollout Time",
});

interface Props {
  platformId: PlatformId;
  fromVersion: string;
  toVersion: string;
}
const calculateDaysElapsed = (
  end: number | undefined,
  start: number | undefined,
): number => (end && start ? (end - start) / (60 * 60 * 24) : -1);

const calculateReleaseStats = (entries: ReleaseHistoryEntry[]) => {
  if (!entries.length) {
    return null;
  }

  const processedEntries = entries.map(entry => ({
    iosPatchCount: entry.version.build ?? -1,
    kickoffTo1: calculateDaysElapsed(
      entry.timestamp1Percent,
      entry.timestampKickoff,
    ),
    kickoffTo2: calculateDaysElapsed(
      entry.timestamp2Percent,
      entry.timestampKickoff,
    ),
    kickoffTo10: calculateDaysElapsed(
      entry.timestamp10Percent,
      entry.timestampKickoff,
    ),
    kickoffTo99: calculateDaysElapsed(
      entry.timestamp99Percent,
      entry.timestampKickoff,
    ),
    kickoffToComplete: calculateDaysElapsed(
      entry.timestampComplete,
      entry.timestampKickoff,
    ),
    patchCount: entry.version.patch ?? -1,
  }));

  const calculateStats = (values: number[]) => {
    const filtered = values.filter(time => time >= 0);
    if (!filtered.length) {
      return null;
    }

    const sorted = [...filtered].sort((a, b) => a - b);
    return {
      avg: (filtered.reduce((a, b) => a + b, 0) / filtered.length).toFixed(1),
      max: sorted[sorted.length - 1].toFixed(1),
      median:
        sorted.length % 2 === 0
          ? (
              (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) /
              2
            ).toFixed(1)
          : sorted[Math.floor(sorted.length / 2)].toFixed(1),
      min: sorted[0].toFixed(1),
    };
  };

  return {
    iosPatchCount: calculateStats(processedEntries.map(t => t.iosPatchCount)),
    kickoffTo1: calculateStats(processedEntries.map(t => t.kickoffTo1)),
    kickoffTo2: calculateStats(processedEntries.map(t => t.kickoffTo2)),
    kickoffTo10: calculateStats(processedEntries.map(t => t.kickoffTo10)),
    kickoffTo99: calculateStats(processedEntries.map(t => t.kickoffTo99)),
    kickoffToComplete: calculateStats(
      processedEntries.map(t => t.kickoffToComplete),
    ),
    patchCount: calculateStats(processedEntries.map(t => t.patchCount)),
  };
};

const getOptions = (
  platformId: PlatformId,
  entries: ReleaseHistoryEntry[],
) => ({
  chart: {
    spacing: [20, 20, 20, 20],
    style: {
      fontFamily: "inherit",
    },
    type: ChartType.LINE,
  },
  credits: { enabled: false },
  series: [
    {
      color: namedColor("macaw"),
      data: entries
        .filter(
          entry =>
            entry.timestampKickoff !== null && entry.timestampComplete !== null,
        )
        .map(entry => {
          const endTime =
            platformId === PlatformId.Android
              ? entry.timestamp99Percent
              : entry.timestampComplete;
          return [
            entry.timestampKickoff ? entry.timestampKickoff * 1000 : 0,
            calculateDaysElapsed(endTime, entry.timestampKickoff),
          ];
        }),
      marker: {
        symbol: "circle",
      },
      name: "Days to Complete",
      type: ChartType.LINE,
    },
    {
      color: namedColor("fox"),
      data: entries
        .filter(
          entry =>
            entry.timestampKickoff !== null && entry.version.patch !== null,
        )
        .map(entry => [
          entry.timestampKickoff ? entry.timestampKickoff * 1000 : 0,
          platformId === PlatformId.IOS
            ? entry.version.build
            : entry.version.patch,
        ]),
      marker: {
        symbol: "circle",
      },
      name: "Patch Count",
      type: ChartType.LINE,
      yAxis: 1,
    },
  ],
  title: {
    style: {
      fontSize: "28px",
      fontWeight: "500",
    },
    text: "Release Metrics Over Time",
  },
  tooltip: {
    shared: true,
    xDateFormat: "%Y-%m-%d",
  },
  xAxis: {
    gridLineColor: namedColor("swan"),
    gridLineWidth: 1,
    lineColor: namedColor("hare"),
    title: {
      style: {
        color: namedColor("eel"),
        fontSize: "20px",
      },
      text: "Kickoff Date",
    },
    type: "datetime",
  },
  yAxis: [
    {
      gridLineColor: namedColor("swan"),
      labels: {
        style: {
          color: namedColor("macaw"),
        },
      },
      title: {
        style: {
          color: namedColor("macaw"),
          fontSize: "20px",
        },
        text: "Days to Complete",
      },
    },
    {
      gridLineColor: namedColor("swan"),
      labels: {
        style: {
          color: namedColor("fox"),
        },
      },
      opposite: true,
      title: {
        style: {
          color: namedColor("fox"),
          fontSize: "20px",
        },
        text: "Patch Count",
      },
    },
  ],
});

const ReleaseHistoryContent = ({
  platformId,
  fromVersion,
  toVersion,
}: Props) => {
  const { isError, error, isLoading, data } = useQuery<ReleaseHistoryData>({
    queryFn: () => getReleaseHistory(platformId, fromVersion, toVersion),
    queryKey: [platformId, fromVersion, toVersion],
  });
  const errorComponent = useErrorScreen(isError, error);

  if (errorComponent) {
    return errorComponent;
  }

  const columns = [
    versionColumn,
    statusColumn,
    downloadLinkColumn(platformId),
    downloadMappingColumn(platformId),
    patchCountColumn(platformId),
    buildNumberColumn(platformId),
    timestampKickoffColumn,
    timestamp1PercentColumn(platformId),
    timestamp2PercentColumn(platformId),
    timestamp10PercentColumn(platformId),
    timestamp99PercentColumn(platformId),
    timestampCompleteColumn,
    daysToCompleteColumn(platformId),
    releaseManagerColumn,
    qaManagerColumn,
  ];

  return data && data.entries.length > 0 ? (
    <div className={styles.history}>
      {(() => {
        const stats = calculateReleaseStats(data.entries);
        return (
          stats && (
            <div className={styles.statsContainer}>
              <h3>Release Statistics:</h3>
              <div className={styles.stats}>
                {stats.kickoffTo1 && (
                  <div>
                    <span>Kickoff to 1% (days): </span>
                    <span>Min: {stats.kickoffTo1.min}</span>
                    <span>Max: {stats.kickoffTo1.max}</span>
                    <span>Avg: {stats.kickoffTo1.avg}</span>
                    <span>Med: {stats.kickoffTo1.median}</span>
                  </div>
                )}
                {stats.kickoffTo2 && (
                  <div>
                    <span>Kickoff to 2% (days): </span>
                    <span>Min: {stats.kickoffTo2.min}</span>
                    <span>Max: {stats.kickoffTo2.max}</span>
                    <span>Avg: {stats.kickoffTo2.avg}</span>
                    <span>Med: {stats.kickoffTo2.median}</span>
                  </div>
                )}
                {stats.kickoffTo10 && (
                  <div>
                    <span>Kickoff to 10% (days): </span>
                    <span>Min: {stats.kickoffTo10.min}</span>
                    <span>Max: {stats.kickoffTo10.max}</span>
                    <span>Avg: {stats.kickoffTo10.avg}</span>
                    <span>Med: {stats.kickoffTo10.median}</span>
                  </div>
                )}
                {stats.kickoffTo99 && (
                  <div>
                    <span>Kickoff to 99% (days): </span>
                    <span>Min: {stats.kickoffTo99.min}</span>
                    <span>Max: {stats.kickoffTo99.max}</span>
                    <span>Avg: {stats.kickoffTo99.avg}</span>
                    <span>Med: {stats.kickoffTo99.median}</span>
                  </div>
                )}
                {stats.kickoffToComplete && (
                  <div>
                    <span>Kickoff to Complete (days): </span>
                    <span>Min: {stats.kickoffToComplete.min}</span>
                    <span>Max: {stats.kickoffToComplete.max}</span>
                    <span>Avg: {stats.kickoffToComplete.avg}</span>
                    <span>Med: {stats.kickoffToComplete.median}</span>
                  </div>
                )}
                {stats.patchCount && (
                  <div>
                    <span>
                      {platformId === PlatformId.IOS
                        ? "Hotfix Count: "
                        : "Patched builds count: "}
                    </span>
                    <span>Min: {stats.patchCount.min}</span>
                    <span>Max: {stats.patchCount.max}</span>
                    <span>Avg: {stats.patchCount.avg}</span>
                    <span>Med: {stats.patchCount.median}</span>
                  </div>
                )}
                {platformId === PlatformId.IOS && stats.iosPatchCount && (
                  <div>
                    <span>Patched builds count (iOS): </span>
                    <span>Min: {stats.iosPatchCount.min}</span>
                    <span>Max: {stats.iosPatchCount.max}</span>
                    <span>Avg: {stats.iosPatchCount.avg}</span>
                    <span>Med: {stats.iosPatchCount.median}</span>
                  </div>
                )}
              </div>
            </div>
          )
        );
      })()}

      <div className={styles.historyContent}>
        <SortableTable columns={columns} data={data.entries} />
      </div>

      <div className={styles.chartContainer} id="releaseMetrics">
        <Chart
          options={getOptions(platformId, data.entries) as highcharts.Options}
          uniqueChartId="releaseMetrics"
        />
      </div>
    </div>
  ) : isLoading ? (
    <LoadingDots type="screen-white" />
  ) : (
    <DuoErrorScreen
      error={Error("Fail to load release history")}
      errorType={DuoErrorType.UnknownError}
    />
  );
};

export default ReleaseHistoryContent;
